import type { I18n } from "@/i18n";
import type { VoltacallRoute } from "@/router.types";
import type { RouteRecordRaw } from "vue-router";

import { QueryClient } from "@tanstack/vue-query";
import { createRouter, createWebHistory } from "vue-router";

import { loginFromStorage } from "@/app/authentication/hooks/use-login.ts";
import { loadDeprecatedEnv } from "@/env.deprecated.ts";
import { useUserAccess } from "@/hooks/operator/use-user-access.ts";
import { ERouteName } from "@/routeName";
import { formatRouteTitle } from "@/router-title";
import { useAuthenticationStore } from "@/stores/authentication/authentication-store.ts";

// Use this helper to have compiler assistance when defining meta properties
function meta(meta: VoltacallRoute["meta"]): VoltacallRoute["meta"] {
  return meta;
}

const routes = [
  {
    path: "/",
    redirect: { name: ERouteName.LOGIN },
  },
  {
    path: "/authentication/login",
    name: ERouteName.LOGIN,
    component: () => import("@/app/authentication/routes/authentication-login-view.vue"),
  },
  {
    path: "/dashboard",
    name: ERouteName.DASHBOARD,
    component: () => import("@/app/dashboard/routes/dashboard-view.vue"),
    meta: meta({
      root: true,
      auth: {
        service: "phoning",
      },
    }),
  },
  {
    path: "/accommodation",
    redirect: { name: ERouteName.V4H_SITES_SEARCH },
    meta: meta({
      root: true,
      auth: {
        service: "v4hService",
        function: "manageOrganizationSites",
        content: "viewOrganizationSites",
      },
    }),
    children: [
      {
        path: "site",
        name: ERouteName.V4H_SITES_SEARCH,
        component: () => import("@/app/accommodation/sites/routes/search-accommodation-site-view.vue"),
      },
      {
        path: "organization/:organizationId",
        name: ERouteName.V4H_ORGANIZATION_DETAILS,
        component: () =>
          import("@/app/accommodation/organizations/routes/[organizationId]/organization-details-base-route.vue"),
        redirect: { name: ERouteName.V4H_ORGANIZATION_OVERVIEW },
        children: [
          {
            path: "overview",
            component: () =>
              import("@/app/accommodation/organizations/routes/[organizationId]/organization-overview-view.vue"),
            name: ERouteName.V4H_ORGANIZATION_OVERVIEW,
          },
          {
            path: "support",
            component: () =>
              import("@/app/accommodation/organizations/routes/[organizationId]/organization-support-view.vue"),
            name: ERouteName.V4H_ORGANIZATION_SUPPORT,
          },
          {
            path: "pilot-oversight",
            component: () =>
              import("@/app/accommodation/organizations/routes/[organizationId]/organization-pilot-oversight-view.vue"),
            name: ERouteName.V4H_ORGANIZATION_PILOT_OVERSIGHT,
            meta: meta({
              if: loadDeprecatedEnv().VITE_ENABLE_ACCOMMODATION_MANAGEMENT,
            }),
          },
        ],
      },
    ],
  },
  {
    path: "/site",
    name: ERouteName.SITE_SEARCH,
    component: () => import("@/app/site/routes/search-site-view.vue"),
  },
  {
    path: "/site/create",
    name: ERouteName.SITE_CREATE,
    component: () => import("@/app/site/routes/create-site-view.vue"),
  },
  {
    path: "/site/:siteId",
    name: ERouteName.SITE_DETAILS,
    component: () => import("@/app/site/routes/[siteId]/routes/site-details-view.vue"),
    redirect: { name: ERouteName.SITE_DETAILS_OVERVIEW },
    children: [
      {
        path: "overview",
        name: ERouteName.SITE_DETAILS_OVERVIEW,
        component: () => import("@/app/site/routes/[siteId]/routes/site-details-overview-view.vue"),
      },
      {
        path: "gallery",
        name: ERouteName.SITE_DETAILS_GALLERY,
        component: () => import("@/app/site/routes/[siteId]/routes/site-details-gallery-view.vue"),
      },
      {
        path: "documents",
        name: ERouteName.SITE_DETAILS_DOCUMENTS,
        component: () => import("@/app/site/routes/[siteId]/routes/site-details-documents-view.vue"),
      },
      {
        path: "support",
        name: ERouteName.SITE_DETAILS_SUPPORT,
        component: () => import("@/app/site/routes/[siteId]/routes/site-details-support-view.vue"),
      },
      {
        path: "rotation",
        name: ERouteName.SITE_DETAILS_ROTATION,
        component: () => import("@/app/site/routes/[siteId]/routes/site-details-rotation-view.vue"),
      },
      {
        path: "consumption",
        name: ERouteName.SITE_DETAILS_CONSUMPTION,
        component: () => import("@/app/site/routes/[siteId]/routes/site-details-consumption-view.vue"),
      },
      {
        path: "livevx",
        name: ERouteName.SITE_DETAILS_LIVE_VX,
        component: () => import("@/app/site/routes/[siteId]/routes/site-details-livevx-view.vue"),
        meta: meta({
          auth: {
            service: "phoning",
            function: "diagnostic",
            content: "liveVX",
          },
        }),
      },
      {
        path: "dtd",
        name: ERouteName.SITE_DETAILS_DTD,
        component: () => import("@/app/site/routes/[siteId]/routes/site-details-dtd-view.vue"),
      },
      {
        path: "operation",
        name: ERouteName.SITE_DETAILS_OPERATION,
        component: () => import("@/app/site/routes/[siteId]/routes/site-details-operation-view.vue"),
      },
      {
        path: "working-condition/modulations",
        name: ERouteName.SITE_DETAILS_WORKING_CONDITION_MODULATIONS,
        component: () =>
          import("@/app/site/routes/[siteId]/routes/working-condition/site-working-condition-modulation-view.vue"),
      },
      {
        path: "working-condition/acquisition",
        name: ERouteName.SITE_DETAILS_WORKING_CONDITION_ACQUISITION,
        component: () =>
          import("@/app/site/routes/[siteId]/routes/working-condition/site-working-condition-acquisition-view.vue"),
      },
      {
        path: "subscriber-feedback",
        name: ERouteName.SITE_DETAILS_SUBSCRIBER_FEEDBACK,
        component: () => import("@/app/site/routes/[siteId]/routes/site-details-subscriber-feedback-view.vue"),
      },
      {
        path: "sensors",
        name: ERouteName.SITE_DETAILS_SENSORS,
        component: () => import("@/app/site/routes/[siteId]/routes/site-details-sensors-view.vue"),
      },
      {
        path: "subscriber/edit",
        name: ERouteName.SITE_DETAILS_SUBSCRIBER_EDIT,
        component: () => import("@/app/site/routes/[siteId]/routes/subscriber/site-details-subscriber-edit-view.vue"),
      },
      {
        path: "site",
        name: ERouteName.SITE_DETAILS_SITE_EDIT,
        component: () => import("@/app/site/routes/[siteId]/routes/site/site-details-site-edit-view.vue"),
      },
      {
        path: "subscriber/attach",
        name: ERouteName.SITE_DETAILS_ATTACH_SUBSCRIBER,
        component: () => import("@/app/site/routes/[siteId]/routes/subscriber/site-details-subscriber-attach-view.vue"),
      },
      {
        path: "personalization",
        name: ERouteName.SITE_DETAILS_COMFORT,
        component: () => import("@/app/site/routes/[siteId]/routes/site-details-comfort-view.vue"),
      },
      {
        path: "appointment",
        name: ERouteName.SITE_DETAILS_APPOINTMENT,
        component: () => import("@/app/site/routes/[siteId]/routes/site-details-appointment-view.vue"),
        meta: meta({
          auth: {
            service: "subscriberManagement",
            function: "site",
            content: "appointment",
          },
        }),
      },
      {
        path: "delivery-point",
        name: ERouteName.SITE_DETAILS_DELIVERY_POINT,
        component: () => import("@/app/site/routes/[siteId]/routes/site-details-delivery-point-view.vue"),
      },
      {
        path: "close-call",
        name: ERouteName.SITE_DETAILS_CLOSE_CALL,
        component: () => import("@/app/site/routes/[siteId]/routes/site-details-close-call.vue"),
      },
      {
        path: "offers-eligibility",
        name: ERouteName.SITE_DETAILS_OFFERS_ELIGIBILITY,
        component: () => import("@/app/site/routes/[siteId]/routes/site-details-offers-eligibility-view.vue"),
      },
      {
        path: "eligibility",
        name: ERouteName.SITE_DETAILS_ELIGIBILITY_FORM,
        component: () => import("@/app/site/routes/[siteId]/routes/site-details-eligibility-view.vue"),
      },
      {
        path: "esc-folder",
        name: ERouteName.SITE_DETAILS_ESC_FODLER,
        component: () => import("@/app/site/routes/[siteId]/routes/site-details-esc-file-view.vue"),
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/",
  },
] as const satisfies (RouteRecordRaw & VoltacallRoute)[];

export function makeRouter(i18n: I18n, queryClient: QueryClient) {
  const router = createRouter({
    history: createWebHistory(),
    routes,
  });
  const formatTitle = formatRouteTitle(i18n, queryClient);

  router.beforeEach(async (guard) => {
    const authenticationStore = useAuthenticationStore();
    const userAccess = useUserAccess();

    // Login page: let authentication-login-view handle its own loginFromStorage
    if (guard.path === "/" || guard.name === ERouteName.LOGIN) {
      return;
    }

    // All other routes: ensure authenticated
    if (!authenticationStore.loggedIn) {
      try {
        await loginFromStorage();
      } catch {
        authenticationStore.logout();
        const redirect = encodeURIComponent(guard.fullPath);
        return { name: ERouteName.LOGIN, query: { redirect } };
      }
    }

    const metaAccess = (guard.meta as VoltacallRoute["meta"] | undefined)?.auth;
    const isUserAuthorized = metaAccess ? userAccess.hasAccess(metaAccess) : true; // By default, everything is authorized

    // Feature flag check
    if (guard.meta.if !== undefined && guard.meta.if === false) {
      return router.currentRoute.value.redirectedFrom ?? { name: ERouteName.DASHBOARD };
    }

    // Access check
    if (!isUserAuthorized) {
      if (guard.name === ERouteName.DASHBOARD) {
        console.error("Access denied to dashboard, redirecting to login");
        return { name: ERouteName.LOGIN };
      } else {
        return { name: ERouteName.DASHBOARD };
      }
    }
  });

  function updateTitle(route = router.currentRoute.value) {
    document.title = formatTitle(route);
  }

  router.afterEach((to, _from, failure) => {
    if (!failure) updateTitle(to);
  });

  // Title sometimes depend on query data : on first page load said data is not available yet
  // So we refresh on specific queries (grep 'updatesRouteTitle' in the codebase)
  queryClient.getQueryCache().subscribe((event) => {
    if (event.type === "updated" && event.action.type === "success" && event.query.meta?.updatesRouteTitle === true) {
      updateTitle();
    }
  });

  return router;
}
